/**
 * Bâtiments vus comme des services : comptoirs à file d'attente (caisses,
 * location de skis), restaurants, accueil. Fonctions partagées pour que le
 * client place les files exactement où le serveur les gère, et nomme les
 * bâtiments de la même façon dans le HUD et dans l'historique.
 */
import { BUILDINGS, type BuildingKind } from './catalog';
import type { Vec2 } from './geometry';
import { DAY_SCALE } from './time';

export type CounterService = 'ticket' | 'rental';

interface Placed {
  kind: BuildingKind;
  x: number;
  z: number;
  rot: number;
}

/** Service à file d'attente rendu par ce type de bâtiment, ou null. */
export function counterService(kind: BuildingKind): CounterService | null {
  const s = BUILDINGS[kind];
  if (s.ticketsPerHour) return 'ticket';
  if (s.rentalsPerHour) return 'rental';
  return null;
}

/** Débit nominal du comptoir (clients par heure de jeu). */
export function counterRate(kind: BuildingKind): number {
  const s = BUILDINGS[kind];
  return s.ticketsPerHour ?? s.rentalsPerHour ?? 0;
}

/** Clients servis par seconde de simulation à l'échelle de temps `scale`. */
export function counterRatePerSimSecond(kind: BuildingKind, scale: number = DAY_SCALE): number {
  return (counterRate(kind) * scale) / 3600;
}

/** Attente (s de simulation) d'un visiteur qui trouve `ahead` personnes devant lui. */
export function counterWait(kind: BuildingKind, ahead: number): number {
  const r = counterRatePerSimSecond(kind);
  return r > 0 ? ahead / r : Infinity;
}

/** Porte d'un bâtiment (devant la façade, axe +z local). */
export function doorOf(b: Placed): Vec2 {
  const d = BUILDINGS[b.kind].footprint[1] / 2 + 3;
  return [b.x + Math.sin(b.rot) * d, b.z + Math.cos(b.rot) * d];
}

/** Personnes par rang de file, selon la largeur de la façade. */
function lineWidth(kind: BuildingKind): number {
  return Math.max(3, Math.min(7, Math.floor(BUILDINGS[kind].footprint[0] / 1.3)));
}

/**
 * Place n° `rank` de la file devant la porte : rangs parallèles à la façade,
 * parcourus en serpentin, de plus en plus loin du bâtiment. [x, z].
 */
export function counterSlot(b: Placed, rank: number): Vec2 {
  const cols = lineWidth(b.kind);
  const row = Math.floor(rank / cols);
  let col = rank % cols;
  if (row % 2 === 1) col = cols - 1 - col;
  const depth = BUILDINGS[b.kind].footprint[1] / 2 + 2.2 + row * 1.35;
  const lateral = (col - (cols - 1) / 2) * 1.15;
  // Axe +z local (façade) : (sin, cos) ; axe +x local : (cos, -sin).
  const fx = Math.sin(b.rot);
  const fz = Math.cos(b.rot);
  return [b.x + fx * depth + Math.cos(b.rot) * lateral, b.z + fz * depth - Math.sin(b.rot) * lateral];
}

/**
 * Nom affiché d'un bâtiment : le premier de son type garde le nom du
 * catalogue (« Caisses »), les suivants sont numérotés (« Caisses 2 »)
 * par ordre de construction.
 */
export function buildingName(b: { id: number; kind: BuildingKind }, all: Iterable<{ id: number; kind: BuildingKind }>): string {
  let rank = 1;
  for (const o of all) if (o.kind === b.kind && o.id < b.id) rank++;
  const label = BUILDINGS[b.kind].label;
  return rank > 1 ? `${label} ${rank}` : label;
}

/** Skieurs qu'une piste accueille en même temps sans gêne (un pour 350 m²). */
export function pisteComfort(p: { length: number; width: number }): number {
  return Math.max(10, Math.round((p.length * p.width) / 350));
}
